/* eslint-disable no-param-reassign */
import find from 'lodash/find';
import { v4 } from 'uuid';
import { Action, Dispatch } from 'redux';
import _ from 'lodash';
import { IAppState } from '../../../state/reducers';

import {
  setClipperCurrentTime,
  pauseAndSetClipperCurrentTime,
  previewVideo,
  startVideoPreviewInterval,
  updateMarkOutOfReviewedVideo,
  playClip,
  stopClip,
} from '../utils/videojs';
import { selectCurrentTime } from './videoPlayer';
import {
  stopClipPreview,
  selectIsClipReviewInProgress,
  selectIsClipReviewing,
  selectCurrentReviewingClip,
} from './currentReviewingClip';
import {
  selectIsClipPlayed,
  updateCurrentPlayingClip,
  resetCurrentPlayingClip,
} from './currentPlayingClip';
import { resetCurrentEditingClipIndex, updateLastEditedClipIndex } from './currentEditingClip';
import {
  createClipList as createClipListServer,
  updateClipList as updateClipListServer,
  IVideoClip,
  getAssetClips as getAssetClipsServer,
} from '../../../api/vault/clipList';
import { reorderListBySortIndex } from '../utils/reorderList';
import { optionsToReactSelect, selectExchangeQualifiersOptions } from '../../../state/lookups';

export enum ActionType {
  UpdateClipLists = 'clipList.UpdateClipLists',
  UpdateUserClips = 'clipList.UpdateUserClips',
  UpdateAllClips = 'clipList.UpdateAllClips',
  ResetClipLists = 'clipList.ResetClipLists',
  UpdateSelectedClipIds = 'clipList.UpdateSelectedClipIds',
}

export interface ITagsAction extends Action<ActionType> {
  payload?: any;
}

export interface IState {
  userClipListId: string,
  userId: number;
  allClips: IVideoClip[],
  selectedClipIds: string[],
}

const INITIAL_STATE: IState = {
  userClipListId: '',
  userId: null,
  allClips: [],
  selectedClipIds: [],
};

const INITIAL_CLIP_STATE: IVideoClip = {
  id: null,
  name: 'new clip',
  markInTime: null,
  markOutTime: null,
  sortIndex: null,
};
const PRE_ROLL_IN_SECONDS = 15;
const SMALLEST_MARK_IN_TIME = 0.01;

export default function reducer(state: IState = INITIAL_STATE, action: ITagsAction) {
  switch (action.type) {
    case ActionType.UpdateClipLists:
      return {
        ...state,
        userClipListId: action.payload.userClipListId,
        userId: action.payload.userId,
        allClips: action.payload.allClips,
      };
    case ActionType.UpdateAllClips:
      return {
        ...state,
        allClips: action.payload,
      };
    case ActionType.UpdateSelectedClipIds:
      return {
        ...state,
        selectedClipIds: [...action.payload],
      };
    case ActionType.ResetClipLists:
      return INITIAL_STATE;
    default:
      return state;
  }
}

const addClipToList = (clip: IVideoClip) => async (
  dispatch: Dispatch,
  getState: () => IAppState,
) => {
  const state = getState();
  const { clipList: { allClips }, clipperMediaContent: { isExchangeLiveEvent } } = state;
  if (isExchangeLiveEvent && clip) {
    const options = optionsToReactSelect(selectExchangeQualifiersOptions(state));
    if (options.length > 0) {
      const [firstOption] = options;
      if (firstOption.value) {
        clip.name = firstOption.value;
      } else if (firstOption.options && firstOption.options.length > 0) {
        const [childOption] = firstOption.options;
        clip.name = childOption.value;
      }
    }
  }
  allClips.push(clip);
  const index = allClips.findIndex((c) => c.id === clip.id);
  dispatch(updateLastEditedClipIndex(index));
  updateClipListOnServer(allClips)(dispatch, getState);

  dispatch({
    type: ActionType.UpdateAllClips,
    payload: allClips,
  });
};

export const removeClipFromList = (clip: IVideoClip) => async (
  dispatch: Dispatch,
  getState: () => IAppState,
) => {
  const { clipList: { allClips, selectedClipIds } } = getState();
  if (clip.userId) return; // Don't delete shared clips
  const index = allClips.findIndex((c) => c.id === clip.id);
  const selectedIndex = selectedClipIds.findIndex((c) => c === clip.id);
  if (selectedIndex !== -1) {
    selectedClipIds.splice(selectedIndex, 1);
  }
  allClips.splice(index, 1);
  updateClipListOnServer(allClips)(dispatch, getState);

  dispatch({
    type: ActionType.UpdateAllClips,
    payload: allClips,
  });
  dispatch({
    type: ActionType.UpdateSelectedClipIds,
    payload: selectedClipIds,
  });
};

export const getClipList = (assetId: string) => async (
  dispatch: Dispatch,
  getState: () => IAppState,
) => {
  const clipLists = (await getAssetClipsServer(assetId)).data.data;
  if (!clipLists.userClipList) {
    clipLists.userClipList = (await createClipListServer({
      assetId,
      clips: [],
    })).data.data;
  }
  dispatch({
    type: ActionType.UpdateClipLists,
    payload: {
      allClips: clipLists.allClips,
      userClipListId: clipLists.userClipList._id,
      userId: clipLists.userClipList.userId,
    },
  });
};

export const updateAllClips = (clips: IVideoClip[]) => ({
  type: ActionType.UpdateAllClips,
  payload: clips,
});

export const updateClipsOrder = (sourceIndex: number, destIndex: number) => async (
  dispatch: Dispatch,
  getState: () => IAppState,
) => {
  const { clipList: { allClips } } = getState();

  const orderedClips = reorderListBySortIndex(allClips, sourceIndex, destIndex) as IVideoClip[];
  dispatch(updateAllClips(orderedClips));
  updateClipListOnServer(orderedClips)(dispatch, getState);
};

export const selectIsInit = (state: IAppState) => {
  const clipList = state?.clipList;
  return !!(clipList?.allClips);
};

export const selectIsAnySelected = (state: IAppState) => {
  const selectedClipIds = state?.clipList?.selectedClipIds;
  if (selectIsInit(state) && selectedClipIds) return selectedClipIds.length > 0;
  return false;
};

export const selectDurationOfSelectedClips = (state: IAppState) => {
  const allClips = state?.clipList?.allClips;
  const selectedClipIds = state?.clipList?.selectedClipIds;
  let duration = 0;
  if (selectIsInit(state) && allClips && selectedClipIds) {
    allClips.forEach((clip) => {
      if (selectedClipIds.includes(clip.id)) {
        duration += clip.markOutTime - clip.markInTime;
      }
    });
  }
  return duration;
};

export const selectSelectedClips = (state: IAppState) => {
  const allClips = state?.clipList?.allClips;
  const selectedClipIds = state?.clipList?.selectedClipIds;
  if (selectIsInit(state) && allClips && selectedClipIds) {
    return allClips.filter((c) => selectedClipIds.includes(c.id));
  }
  return [];
};

const updateClipListOnServer = (clips: IVideoClip[]) => async (
  dispatch: Dispatch,
  getState: () => IAppState,
) => {
  // Filter to get only user clips
  const userClips = clips.filter((clip) => !clip.userId);
  const { clipList: { userClipListId } } = getState();
  const clipsCopy = _.cloneDeep(userClips);
  const updated = await updateClipListServer(userClipListId, {
    clips: clipsCopy,
  });
};

export const isClipSelected = (clipId: string) => (
  dispatch: Dispatch,
  getState: () => IAppState,
) => {
  const { clipList: { selectedClipIds } } = getState();
  return selectedClipIds.includes(clipId);
};

export const toggleClipSelection = (clipIndex: number) => async (
  dispatch: Dispatch,
  getState: () => IAppState,
) => {
  const { clipList: { allClips, selectedClipIds } } = getState();
  const clip = allClips[clipIndex];
  const foundIndex = selectedClipIds.findIndex((val) => val === clip.id);
  if (foundIndex !== -1) {
    selectedClipIds.splice(foundIndex, 1);
  } else {
    selectedClipIds.push(clip.id);
  }
  dispatch({
    type: ActionType.UpdateSelectedClipIds,
    payload: selectedClipIds,
  });
};

export const resetClipList = () => ({
  type: ActionType.ResetClipLists,
});

export const setAllClipsSelected = (isSelected: boolean) => (
  dispatch: Dispatch,
  getState: () => IAppState,
) => {
  const state = getState();
  if (selectIsInit(state)) {
    const { clipList: { allClips } } = state;
    let selectedClipIds: string[] = [];
    if (isSelected) selectedClipIds = allClips.map((clip) => (clip.id));
    dispatch({
      type: ActionType.UpdateSelectedClipIds,
      payload: selectedClipIds,
    });
  }
};

export interface IClipFieldUpdates {
  name?: string;
  markInTime?: number;
  markOutTime?: number;
  sortIndex?: number;
}

export const updateClipFields = (clipIndex: number, updates: IClipFieldUpdates) => (
  dispatch: Dispatch,
  getState: () => IAppState,
) => {
  const { clipList: { userId, allClips } } = getState();

  if (allClips[clipIndex].userId && allClips[clipIndex].userId !== userId) {
    console.log('[QA] Can\'t update fields of clips not owned by user');
  }

  allClips[clipIndex] = {
    ...allClips[clipIndex],
    ...updates,
  };

  dispatch(updateLastEditedClipIndex(clipIndex));
  updateClipListOnServer(allClips)(dispatch, getState);
  dispatch(updateAllClips(allClips));
};

export enum MarkTime {
  In = 'markInTime',
  Out = 'markOutTime',
}

const setClipTimeAsCurrentTime = (clipIndex: number, time: MarkTime) => (
  dispatch: Dispatch,
  getState: () => IAppState,
) => {
  let currentTime = selectCurrentTime(getState());
  if (currentTime === 0) currentTime = SMALLEST_MARK_IN_TIME;
  updateClipFields(clipIndex, {
    [time.toString()]: currentTime,
  })(dispatch, getState);
};

export function setClipMarkTimeAsCurrentTime(clipIndex: number, time: MarkTime) {
  return setClipTimeAsCurrentTime(clipIndex, time);
}

const isClipListEmpty = (state: IState) => selectUserClips(state.allClips).length === 0;

// Clips without userId are owned by current user
export const selectUserClips = (allClips: IVideoClip[]) => (allClips ? allClips.filter((c) => !c.userId) : []);

// https://stackoverflow.com/questions/75129682/how-does-typescript-fit-this-method
// Can't use findLastIndex and findLast because of "npm run-script tsc:check"
const selectLastUserClipIndex = (state: IState) => {
  const index = [...state.allClips].reverse().findIndex((clip) => !clip.userId);
  const count = state.allClips.length - 1;
  return index >= 0 ? count - index : index;
};

const selectLastUserClip = (state: IState) => state.allClips[selectLastUserClipIndex(state)];

const isLastClipIntervalSet = (state: IState) => {
  const lastClip = selectLastUserClip(state);
  return lastClip.markInTime && lastClip.markOutTime;
};

const getNewClipStateWithOwner = (state: IState): IVideoClip => {
  const { allClips } = state;
  const clips = selectUserClips(allClips);
  const sortIndex = allClips && allClips.length > 0 ? (Math.max(...clips.map((o) => o.sortIndex), allClips.length) + 1) : 1;
  return {
    ...INITIAL_CLIP_STATE,
    id: v4(),
    sortIndex,
  };
};

const getNewMarkInTime = (markOutTime: number) => {
  const markInTime = markOutTime - PRE_ROLL_IN_SECONDS;
  return markInTime > 0 ? markInTime : SMALLEST_MARK_IN_TIME;
};

const addNewClipMarkWithCurrentTime = (time: MarkTime) => (
  dispatch: Dispatch,
  getState: () => IAppState,
) => {
  const state = getState();
  const newClip: IVideoClip = { ...getNewClipStateWithOwner(state.clipList) };
  let currentTime = selectCurrentTime(state);
  if (currentTime === 0) currentTime = SMALLEST_MARK_IN_TIME;
  if (time === MarkTime.In) {
    newClip.markInTime = currentTime;
  } else {
    newClip.markInTime = getNewMarkInTime(currentTime);
    newClip.markOutTime = currentTime;
  }
  addClipToList(newClip)(dispatch, getState);
};

export const handleMarkNewClip = (time: MarkTime) => (
  dispatch: Dispatch,
  getState: () => IAppState,
) => {
  const state = getState();
  const { currentEditingClip, clipList } = state;
  const currentTime = selectCurrentTime(state);
  if (currentEditingClip.currentIndex !== -1) {
    const { markInTime, markOutTime } = clipList.allClips[currentEditingClip.currentIndex];
    let disabled = false;
    if (time === MarkTime.In) disabled = markOutTime !== null && currentTime >= markOutTime;
    else disabled = markInTime === null || currentTime <= markInTime;
    if (!disabled) {
      setClipMarkTime(currentEditingClip.currentIndex, time)(dispatch, getState);
      dispatch(resetCurrentEditingClipIndex());
    }
  } else if (isClipListEmpty(clipList) || isLastClipIntervalSet(clipList)) {
    addNewClipMarkWithCurrentTime(time)(dispatch, getState);
  } else {
    setClipMarkTimeAsCurrentTime(selectLastUserClipIndex(clipList), time)(dispatch, getState);
  }
};

export function selectIsMarkOutDisabled(state: IAppState) {
  return !state.videoPlayer.isReady
    || (!isClipListEmpty(state.clipList)
      && !isLastClipIntervalSet(state.clipList)
      && selectCurrentTime(state) < selectLastUserClip(state.clipList)?.markInTime);
}

export const navigateToMarkIOTime = (time: number, clipIndex: number) => (
  dispatch: Dispatch,
  getState: () => IAppState,
) => {
  const state = getState();
  const isClipReviewInProgress = selectIsClipReviewInProgress(state);

  if (isClipReviewInProgress) {
    const isThisClipReviewed = selectIsClipReviewing(state, clipIndex);
    if (isThisClipReviewed) {
      const { markInTime, markOutTime } = selectCurrentReviewingClip(state);
      previewVideo(markInTime, markOutTime);
    } else {
      stopClipPreview()(dispatch, getState);
      setClipperCurrentTime(time);
    }
  } else {
    pauseAndSetClipperCurrentTime(time);
  }
};

const findNextSelectedClip = (state: IState, fromIndex: number) => {
  let nextClipIndex: number;
  const nextClip = find(state.allClips, (clip, i) => {
    if (state.selectedClipIds.includes(clip.id) && clip.markInTime !== null && clip.markOutTime !== null) {
      nextClipIndex = i;
      return true;
    }
    return false;
  }, fromIndex);

  return {
    nextClipIndex,
    nextClip,
  };
};

const playSelectedClipsFrom = (clipIndex: number) => async (
  dispatch: Dispatch,
  getState: () => IAppState,
) => {
  const state = getState();
  // Please note that getState() should be called before starting the next clip to get the current state
  // as mark in/out of the next clip could be updated while previous clips are played
  const { nextClip, nextClipIndex } = findNextSelectedClip(state.clipList, clipIndex);

  if (nextClip) {
    dispatch(updateCurrentPlayingClip(nextClip, nextClipIndex));
    const { markInTime, markOutTime } = nextClip;
    await playClip(markInTime, markOutTime);
    playSelectedClipsFrom(nextClipIndex + 1)(dispatch, getState);
  } else {
    dispatch(resetCurrentPlayingClip());
  }
};

export const setClipMarkTime = (clipIndex: number, time: MarkTime) => (
  dispatch: Dispatch,
  getState: () => IAppState,
) => {
  const state = getState();
  const isThisClipReviewed = selectIsClipReviewing(state, clipIndex);
  const isThisClipPlayed = selectIsClipPlayed(state, clipIndex);
  let currentTime = selectCurrentTime(state);
  if (currentTime === 0) currentTime = SMALLEST_MARK_IN_TIME;
  if (isThisClipPlayed && time === MarkTime.Out) {
    stopClip();
    setClipMarkTimeAsCurrentTime(clipIndex, time)(dispatch, getState);
    playSelectedClipsFrom(clipIndex + 1)(dispatch, getState);
  } else if (isThisClipReviewed) {
    updateClipFields(clipIndex, { [time.toString()]: currentTime })(dispatch, getState);
    const { markInTime, markOutTime } = selectCurrentReviewingClip(state);
    if (time === MarkTime.In) {
      startVideoPreviewInterval(currentTime, markOutTime);
    } else {
      updateMarkOutOfReviewedVideo(markInTime, currentTime);
    }
  } else {
    setClipMarkTimeAsCurrentTime(clipIndex, time)(dispatch, getState);
  }
};

export const playSelectedClips = () => (
  dispatch: Dispatch,
  getState: () => IAppState,
) => {
  stopClipPreview()(dispatch, getState);
  playSelectedClipsFrom(0)(dispatch, getState);
};

export const stopSelectedClips = () => (
  dispatch: Dispatch,
  getState: () => IAppState,
) => {
  stopClip();
  dispatch(resetCurrentPlayingClip());
};

export const extendLastEditiedClip = () => (
  dispatch: Dispatch,
  getState: () => IAppState,
) => {
  const { currentEditingClip: { lastEditedIndex }, clipList: { allClips } } = getState();
  if (lastEditedIndex !== -1) {
    const clip = allClips[lastEditedIndex];
    let newMarkOutTime: number;
    if (clip.markOutTime) newMarkOutTime = clip.markOutTime + 5; // Extend out five seconds
    else if (clip.markInTime) newMarkOutTime = clip.markInTime + 5; // Extend out five seconds
    if (newMarkOutTime) {
      pauseAndSetClipperCurrentTime(newMarkOutTime);
      updateClipFields(lastEditedIndex, { [MarkTime.Out.toString()]: newMarkOutTime })(dispatch, getState);
    }
  }
};

export function selectTitleForNewAsset(state: IAppState) {
  const { clipList: { allClips, selectedClipIds }, clipperMediaContent: { assetDetails } } = state;
  if (selectIsInit(state)) {
    const selected = allClips.filter((clip) => selectedClipIds.includes(clip.id));
    if (selected.length !== 1 && assetDetails?.title) {
      return `${assetDetails.title} Clip`;
    }
    if (selected.length > 0) {
      const [{ name }] = selected;
      return name;
    }
  }
  return '';
}
